import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, User, Mail, Briefcase, FileCheck2, X, Sliders, Settings, ExternalLink, Filter, ChevronDown, ChevronRight, Building2, Zap, CreditCard } from 'lucide-react';
import Logo from '../../components/Logo';

const DashboardSidebar = ({ isMobileOpen, onCloseMobile, activeTab, setTab }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to check if a path belongs to a group
  const isSettingsPath = (path) => 
    path === '/settings' || path === '/form-control' || path === '/filter-settings' || path === '/external-apply';
    
  const isResumePath = (path, currentTab) => 
    (path === '/dashboard' && (currentTab === 'builder' || currentTab === 'resumes' || currentTab === 'letters'));

  const isEmailPath = (path) => 
    path === '/email' || path === '/find-hr';

  // Initialize states based on current location
  const [settingsExpanded, setSettingsExpanded] = useState(() => isSettingsPath(location.pathname));
  const [resumeExpanded, setResumeExpanded] = useState(() => {
    // Need to check location state or activeTab prop for initial load if on dashboard
    const stateTab = location.state?.tab || activeTab;
    return isResumePath(location.pathname, stateTab);
  });
  const [emailRecruitExpanded, setEmailRecruitExpanded] = useState(() => isEmailPath(location.pathname));

  // Reordered: Settings first, then Job Agent, then others, with Pricing before Profile
  const navItems = [
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: Settings, 
      path: '/settings',
      subItems: [
        { id: 'form-control', label: 'Form Control', icon: Sliders, path: '/form-control' },
        { id: 'filter-settings', label: 'Filter Settings', icon: Filter, path: '/filter-settings' },
        { id: 'external-apply', label: 'External Apply', icon: ExternalLink, path: '/external-apply' },
      ]
    },
    { id: 'job-agent', label: 'AI Job Agent', icon: Zap, path: '/job-agent' },
    {
      id: 'resume',
      label: 'Resume',
      icon: FileText,
      path: '/dashboard',
      subItems: [
        { id: 'builder', label: 'Resume Builder', icon: FileText, tab: 'builder' },
        { id: 'resumes', label: 'My Resumes', icon: FileCheck2, tab: 'resumes' },
        { id: 'letters', label: 'Cover Letters', icon: Mail, tab: 'letters' },
      ],
    },
    { id: 'job-tracker', label: 'Job Tracker', icon: Briefcase, path: '/job-tracker' },
    {
      id: 'email-recruit',
      label: 'Email Recruiter',
      icon: Mail,
      path: '/email',
      subItems: [
        { id: 'email', label: 'Email', icon: Mail, path: '/email' },
        { id: 'find-hr', label: 'Find Recruiter', icon: Building2, path: '/find-hr' },
      ],
    },
    { id: 'pricing', label: 'Pricing', icon: CreditCard, path: '/pricing' },
    { id: 'profile', label: 'Profile', icon: User, path: '/profile' },
  ];

  const handleNavClick = (item, subItem = null) => {
    // If clicking a sub-item, just navigate and close mobile if needed
    if (subItem) {
      if (item.id === 'resume') {
        navigate('/dashboard', { state: { tab: subItem.tab } });
        if (setTab) setTab(subItem.tab);
      } else {
        navigate(subItem.path);
      }
      if (onCloseMobile) onCloseMobile();
      return;
    }

    // Main Item Click Logic
    if (item.subItems) {
      // Toggle logic
      if (item.id === 'settings') {
        // If we are mostly just toggling:
        if (settingsExpanded && isSettingsPath(location.pathname)) {
            setSettingsExpanded(false);
        } else {
            setSettingsExpanded(true);
            navigate('/settings');
        }
      } else if (item.id === 'resume') {
        if (resumeExpanded && isResumePath(location.pathname, activeTab)) {
            setResumeExpanded(false);
        } else {
            setResumeExpanded(true);
            navigate('/dashboard', { state: { tab: 'builder' } });
            if (setTab) setTab('builder');
        }
      } else if (item.id === 'email-recruit') {
        if (emailRecruitExpanded && isEmailPath(location.pathname)) {
            setEmailRecruitExpanded(false);
        } else {
            setEmailRecruitExpanded(true);
            navigate('/email');
        }
      }
      return;
    }
    
    // Standard Item (No Subitems)
    navigate(item.path);
    if (onCloseMobile) onCloseMobile();
  };

  // Determine active state for highlighting
  const getActiveState = (item) => {
    if (item.subItems) {
       // Check if any child is active OR if the parent path maps strictly
       // But simpler: Check our known expansion/active logic or path matching
       if (item.id === 'settings') return isSettingsPath(location.pathname);
       if (item.id === 'email-recruit') return isEmailPath(location.pathname);
       if (item.id === 'resume') return isResumePath(location.pathname, activeTab || location.state?.tab);
    }
    
    // Direct matches
    if (item.id === 'job-agent') return location.pathname === '/job-agent';
    if (item.id === 'job-tracker') return location.pathname === '/job-tracker';
    if (item.id === 'pricing') return location.pathname === '/pricing';
    
    return false;
  };

  const getSubItemActive = (subItem) => {
      // Logic for sub-item active state
      if (subItem.tab) return (activeTab === subItem.tab);
      return location.pathname === subItem.path;
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.03)]">
      {/* Header Section - Improved */}
      <div className="flex items-center gap-3 px-6 h-20 shrink-0 border-b border-slate-100/80">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            <Logo className="w-auto h-9" />
          </div>
          <div className="flex flex-col justify-center min-w-0">
            <div className="text-[9px] text-blue-600 font-black uppercase tracking-[0.15em] leading-tight">Dashboard</div>
          </div>
        </div>
      </div>

      {/* Navigation Section - Enhanced */}
      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1 touch-pan-y scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
        {navItems.map((item) => {
          const isActive = getActiveState(item);
          // Use manual state
          let isExpanded = false;
          if (item.id === 'settings') isExpanded = settingsExpanded;
          if (item.id === 'resume') isExpanded = resumeExpanded;
          if (item.id === 'email-recruit') isExpanded = emailRecruitExpanded;
          
          const Icon = item.icon;

          return (
            <div key={item.id} className="relative">
              <button
                type="button"
                onClick={() => handleNavClick(item)}
                className={`w-full flex items-center justify-between px-4 py-3.5 text-[15px] font-semibold rounded-xl transition-all duration-200 group relative active:scale-[0.98] ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 shadow-sm' 
                    : 'text-slate-700 hover:bg-slate-50/80 hover:text-slate-900'
                }`}
              >
                {/* Active Indicator Line (Left) - Enhanced */}
                {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-blue-600 rounded-r-full shadow-sm" />
                )}
                
                <div className="flex items-center gap-3.5 flex-1 min-w-0">
                  <Icon 
                    className={`w-5 h-5 flex-shrink-0 transition-all duration-200 ${
                        isActive 
                          ? 'text-blue-600' 
                          : 'text-slate-500 group-hover:text-slate-700'
                    }`} 
                    strokeWidth={isActive ? 2.5 : 2}     
                  />
                  <span className={`truncate ${isActive ? 'font-bold' : 'font-semibold'}`}>{item.label}</span>
                </div>

                {item.subItems && (
                  <div className={`flex-shrink-0 text-slate-400 transition-all duration-200 p-1 ml-2 ${
                    isExpanded ? 'rotate-180 text-blue-600' : 'group-hover:text-slate-600'
                  }`}>
                    <ChevronDown size={16} strokeWidth={2.5} />
                  </div>
                )}
              </button>

              {/* Submenu - Enhanced */}
              {item.subItems && isExpanded && (
                <div className="mt-1 mb-2 ml-4 pl-5 border-l-2 border-slate-200/60 space-y-0.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  {item.subItems.map((subItem) => {
                    const isSubActive = getSubItemActive(subItem);
                    const SubIcon = subItem.icon;
                    return (
                      <button
                        key={subItem.id}
                        type="button"
                        onClick={(e) => {
                           e.stopPropagation();
                           handleNavClick(item, subItem);
                        }}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm rounded-lg transition-all duration-200 active:scale-[0.98] ${
                          isSubActive
                            ? 'bg-blue-50 text-blue-700 font-bold shadow-sm' 
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50/60'
                        }`}
                      >
                         <SubIcon 
                           size={17} 
                           className={`flex-shrink-0 transition-colors ${
                             isSubActive ? 'text-blue-600' : 'text-slate-400'
                           }`} 
                           strokeWidth={isSubActive ? 2.5 : 2}
                         />
                        <span className="truncate font-medium">{subItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer Section - Enhanced */}
      <div className="p-5 border-t border-slate-100/80 bg-slate-50/30">
        <div className="text-[10px] text-center text-slate-400 font-semibold tracking-wide">
          © {new Date().getFullYear()} AI Job Mate
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar - hidden on mobile, visible on lg */}
      <aside className="hidden lg:block w-64 h-full shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden backdrop-blur-sm transition-opacity" 
          onClick={onCloseMobile} 
          aria-hidden="true" 
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside 
        className={`fixed inset-y-0 left-0 w-[85vw] max-w-[300px] bg-white z-[60] transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] lg:hidden shadow-2xl ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="relative h-full flex flex-col">
            <button
                type="button"
                onClick={onCloseMobile}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 focus:outline-none active:scale-95 transition-transform"
                aria-label="Close navigation"
            >
                <X size={24} />
            </button>
            <div className="flex-1 overflow-hidden">
                <SidebarContent />
            </div>
        </div>
      </aside>
    </>
  );
};

export default DashboardSidebar;


